import { Injectable, computed, signal } from '@angular/core';
import { BoxShape } from './shared/shapes';
import { placeholderKeys } from './shared/placeholders';
import { clamp } from './editor-geometry';
import {
  BackgroundKind,
  CatalogEntry,
  DesignCatalog,
  DesignElement,
  ElementFont,
  LayoutIssue,
  VoucherDesign,
  VoucherType,
} from '../../services/voucher-design.service';
import {
  AlignMode,
  BrandingFiles,
  DistributeAxis,
  ExpiryRule,
  Issue,
  PageSize,
  Rect,
  alignRects,
  allowedOn,
  clampRect,
  distributeRects,
  minSizeFor,
  placeholderMap,
  rectsIntersect,
  requiredFor,
  roundPt,
  selectionBounds,
  validateLayout,
} from './editor-geometry';

/** A change for several elements at once; `font` merges into each element's font. */
export type SelectionPatch = Omit<Partial<DesignElement>, 'font'> & { font?: Partial<ElementFont> };
/** Which of the selected elements a shared change applies to. */
export type PatchScope = 'text' | 'frame' | 'box' | 'qr' | 'opacity';

/** One required thing: an element kind, or a placeholder some text must carry. */
export interface ChecklistEntry {
  kind: 'element' | 'placeholder';
  key: string;
  label: string;
  placedId: string | null;
}

/** What "add" places as a text: its content, whether it wraps, and its size. */
export interface TextPreset {
  content: string;
  wrap: boolean;
  size?: { width: number; height: number };
  /** Open the new text in place with its words selected, so typing replaces them. */
  edit?: boolean;
}

export interface ElementIssues {
  errors: string[];
  warnings: string[];
}

/** The page as the server keeps it: what the page panel changes and an undo restores. */
export interface PageSettings {
  print_scale: number;
  background_kind: BackgroundKind;
  background_template: string | null;
  background_color: string | null;
  page_width_pt: number;
  page_height_pt: number;
  /** Identifies an uploaded file: its preview URL changes with every upload. */
  file_id: string | null;
}

export function pageSettingsOf(design: VoucherDesign): PageSettings {
  return {
    print_scale: design.print_scale,
    background_kind: design.background_kind,
    background_template: design.background_template,
    background_color: design.background_color,
    page_width_pt: design.page_width_pt,
    page_height_pt: design.page_height_pt,
    file_id: design.background_kind === 'file' ? design.preview_url : null,
  };
}

/** One step of the history: the elements and the page as they were. */
interface HistoryEntry {
  elements: string;
  page: PageSettings | null;
}

/** Puts the page back on the server (background, orientation, print size) and saves the elements. */
export type PageRestorer = (
  page: PageSettings,
  elements: DesignElement[],
) => Promise<VoucherDesign>;

/** The editor's state: one design, its elements, the selection and the scale. */
@Injectable()
export class VoucherDesignEditorStore {
  readonly design = signal<VoucherDesign | null>(null);
  readonly catalog = signal<DesignCatalog | null>(null);
  readonly elements = signal<DesignElement[]>([]);
  /** The selected element ids. One element shows the inspector, several the selection panel. */
  readonly selectedIds = signal<ReadonlySet<string>>(new Set());
  /** JSON of the elements as last saved; `dirty` compares against it. */
  readonly savedSnapshot = signal('');
  /** Errors the backend reported on the last save, keyed to elements by index. */
  readonly serverIssues = signal<Issue[]>([]);
  /** The scale at which the whole page fits the viewport; the canvas measures it. */
  readonly fitScale = signal(1);
  /** How big the page is shown: 1 = real size (one CSS inch per inch). */
  readonly zoom = signal(1);
  /** The first measurement after a load picks the starting zoom. */
  private zoomPending = true;
  /** Whether the company has a logo and a banner (for the elements that show them). */
  readonly branding = signal<BrandingFiles>({ logo: true, banner: true });
  /** Show the variable texts at the length they are planned for instead of the samples. */
  readonly longTexts = signal(false);
  /** The legal minimum validity with and without a printed date, when they differ (Italy). */
  readonly legalYears = signal<ExpiryRule | null>(null);
  /** The text being edited in place (a double-click); any other selection ends it. */
  private readonly editingId = signal<string | null>(null);
  /** The editing started with the whole text selected (a fresh text's placeholder words). */
  readonly editSelectAll = signal(false);
  readonly editing = computed(() => {
    const id = this.editingId();
    const ids = this.selectedIds();
    return id && ids.size === 1 && ids.has(id) ? id : null;
  });
  /** Dashed outlines around every element (off: only on hover and selection). */
  readonly showOutlines = signal(false);
  readonly paletteOpen = signal(true);
  readonly inspectorOpen = signal(true);
  readonly canUndo = signal(false);
  readonly canRedo = signal(false);
  /** An undo or redo that waits for the server (a page change). */
  readonly restoring = signal(false);
  /** Snapshots before each change (undo) and after an undo (redo). */
  private past: HistoryEntry[] = [];
  private future: HistoryEntry[] = [];
  /** Set by the editor: how to put a page change back on the server. */
  pageRestorer: PageRestorer | null = null;
  /** Files uploaded in this session, by their id, so an undo can upload them again. */
  private readonly heldFiles = new Map<string, File>();
  /** The elements before a drag started: one history entry per drag, not per move. */
  private dragSnapshot: string | null = null;
  /** The last recorded change, so typing into one field is one entry, not one per key. */
  private lastRecord: { key: string; at: number } | null = null;
  /** While several additions form one history entry. */
  private batching = false;

  readonly page = computed<PageSize>(() => {
    const d = this.design();
    return { width: d?.page_width_pt ?? 595, height: d?.page_height_pt ?? 842 };
  });
  readonly voucherType = computed<VoucherType>(() => this.design()?.voucher_type ?? 'money');
  readonly scale = computed(() => this.zoom());
  readonly selectedElements = computed(() => {
    const ids = this.selectedIds();
    return this.elements().filter((e) => ids.has(e.id));
  });
  /** The whole group the selection lies in (all of it or one part), or null. */
  readonly selectedGroup = computed(() => {
    const selected = this.selectedElements();
    const group = selected[0]?.group;
    if (!group || !selected.every((e) => e.group === group)) return null;
    const members = this.elements().filter((e) => e.group === group);
    return members.length > 1 ? { group, bounds: selectionBounds(members)!, members } : null;
  });
  /** The single selected element, or null (none or several). */
  readonly selected = computed(() => {
    const all = this.selectedElements();
    return all.length === 1 ? all[0] : null;
  });
  readonly selectedId = computed(() => this.selected()?.id ?? null);
  /** The box around the selection, in pt. */
  readonly selectionBounds = computed(() => selectionBounds(this.selectedElements()));
  /** Whether the selection is exactly one whole group. */
  readonly selectionIsGroup = computed(() => {
    const all = this.selectedElements();
    const group = all[0]?.group;
    if (!group || all.length < 2) return false;
    return all.every((e) => e.group === group) && this.membersOf(group).length === all.length;
  });
  readonly catalogByKind = computed(
    () => new Map((this.catalog()?.elements ?? []).map((e) => [e.kind, e])),
  );
  readonly placeholdersByKey = computed(() => placeholderMap(this.catalog()));
  /** The values a text of this design may carry: product values on product designs only. */
  readonly allowedPlaceholders = computed(() => {
    const type = this.voucherType();
    return new Map(
      [...this.placeholdersByKey()].filter(([, p]) => type === 'product' || !p.product_only),
    );
  });
  /** Whether some text prints the expiry date (the template artwork follows it). */
  readonly usesExpiry = computed(() =>
    this.elements().some(
      (e) =>
        e.kind === TEXT_KIND &&
        placeholderKeys(e.content ?? '', this.placeholdersByKey()).includes('expires_at'),
    ),
  );
  readonly selectedEntry = computed(() => {
    const selected = this.selected();
    return selected ? (this.catalogByKind().get(selected.kind) ?? null) : null;
  });
  readonly fontCss = computed(() => {
    const fonts = new Map<string, string>(
      (this.catalog()?.fonts ?? []).map((f) => [f.family, f.css]),
    );
    return (family: string) => fonts.get(family) ?? fonts.get('sans') ?? 'sans-serif';
  });
  readonly dirty = computed(() => JSON.stringify(this.elements()) !== this.savedSnapshot());
  readonly validation = computed(() =>
    validateLayout(
      this.elements(),
      this.catalog(),
      this.page(),
      this.voucherType(),
      this.branding(),
      this.legalYears(),
    ),
  );
  /** The required things in catalog order: placeholders (satisfied by any text that
   * carries them), then element kinds. */
  readonly checklist = computed<ChecklistEntry[]>(() => {
    const catalog = this.catalog();
    if (!catalog) return [];
    const required = requiredFor(catalog, this.voucherType());
    const known = this.placeholdersByKey();
    const elements = this.elements();
    const texts = elements.filter((e) => e.kind === TEXT_KIND);
    const placeholders = catalog.placeholders
      .filter((p) => required.placeholders.includes(p.key))
      .map((p) => ({
        kind: 'placeholder' as const,
        key: p.key,
        label: p.label,
        placedId:
          texts.find((e) => placeholderKeys(e.content ?? '', known).includes(p.key))?.id ?? null,
      }));
    const kinds = catalog.elements
      .filter((entry) => required.elements.includes(entry.kind))
      .map((entry) => ({
        kind: 'element' as const,
        key: entry.kind,
        label: entry.label,
        placedId: elements.find((e) => e.kind === entry.kind)?.id ?? null,
      }));
    return [...placeholders, ...kinds];
  });
  /** Errors and warnings per element, for the badge on the canvas. */
  readonly issuesByElement = computed(() => {
    const byId = new Map<string, ElementIssues>();
    const add = (issue: Issue, severity: 'error' | 'warning') => {
      if (!issue.elementId) return;
      const entry = byId.get(issue.elementId) ?? { errors: [], warnings: [] };
      entry[severity === 'error' ? 'errors' : 'warnings'].push(issue.message);
      byId.set(issue.elementId, entry);
    };
    for (const issue of this.serverIssues()) add(issue, 'error');
    for (const issue of this.validation().errors) add(issue, 'error');
    for (const issue of this.validation().warnings) add(issue, 'warning');
    return byId;
  });
  /** How many elements can be reordered: all but the logo, which stays on top. */
  readonly layerCount = computed(() => this.elements().filter((e) => e.kind !== BRAND_KIND).length);
  readonly canSave = computed(() => this.validation().errors.length === 0);
  /** The layout shows a logo or banner the company has not uploaded: no activation. */
  readonly missingBranding = computed(() =>
    this.validation().warnings.some(
      (w) => w.code === 'missing_logo' || w.code === 'missing_banner',
    ),
  );
  readonly canActivate = computed(
    () => this.canSave() && !this.dirty() && !this.missingBranding() && !this.design()?.is_active,
  );

  init(design: VoucherDesign, catalog: DesignCatalog) {
    this.design.set(design);
    this.catalog.set(catalog);
    const elements = brandLast((design.layout?.elements ?? []).map(cleanElement));
    this.elements.set(elements);
    this.savedSnapshot.set(JSON.stringify(elements));
    this.serverIssues.set([]);
    this.selectedIds.set(new Set());
    this.zoomPending = true;
    this.clearHistory();
  }

  // -------------------------------------------------------------- history

  private snapshot(): HistoryEntry {
    const design = this.design();
    return {
      elements: JSON.stringify(this.elements()),
      page: design ? pageSettingsOf(design) : null,
    };
  }

  private push(stack: HistoryEntry[], entry: HistoryEntry) {
    stack.push(entry);
    if (stack.length > 100) stack.shift();
  }

  /**
   * Remember the elements before a change. Changes with the same `key` within a
   * second merge into one entry (typing, arrow keys), so undo steps stay meaningful.
   */
  private record(key?: string) {
    const now = Date.now();
    if (key && this.lastRecord?.key === key && now - this.lastRecord.at < 1000) {
      this.lastRecord.at = now;
      return;
    }
    this.push(this.past, this.snapshot());
    this.future = [];
    this.lastRecord = key ? { key, at: now } : null;
    this.updateHistoryFlags();
  }

  private clearHistory() {
    this.past = [];
    this.future = [];
    this.dragSnapshot = null;
    this.lastRecord = null;
    this.updateHistoryFlags();
  }

  private updateHistoryFlags() {
    const busy = this.restoring();
    this.canUndo.set(this.past.length > 0 && !busy);
    this.canRedo.set(this.future.length > 0 && !busy);
  }

  private restore(snapshot: string) {
    const elements = JSON.parse(snapshot) as DesignElement[];
    this.elements.set(elements);
    const ids = new Set(elements.map((e) => e.id));
    this.selectedIds.update((current) => new Set([...current].filter((id) => ids.has(id))));
    this.lastRecord = null;
    this.updateHistoryFlags();
  }

  undo() {
    void this.step(this.past, this.future);
  }

  redo() {
    void this.step(this.future, this.past);
  }

  /**
   * One step back or forward. A step whose page differs from the current one
   * (background, orientation, print size) goes through the server first; the
   * elements follow once that succeeded, and on failure the step stays available.
   */
  private async step(from: HistoryEntry[], to: HistoryEntry[]) {
    if (this.restoring()) return;
    const entry = from.pop();
    if (entry === undefined) return;
    const current = this.snapshot();
    const design = this.design();
    if (entry.page && design && !this.samePage(entry.page, pageSettingsOf(design))) {
      if (!this.pageRestorer) {
        from.push(entry);
        return;
      }
      this.restoring.set(true);
      this.updateHistoryFlags();
      try {
        const elements = JSON.parse(entry.elements) as DesignElement[];
        const restored = await this.pageRestorer(entry.page, elements);
        this.design.set(restored);
        this.savedSnapshot.set(JSON.stringify((restored.layout?.elements ?? []).map(cleanElement)));
        this.serverIssues.set([]);
        this.push(to, current);
        this.restore(entry.elements);
      } catch {
        from.push(entry);
      } finally {
        this.restoring.set(false);
        this.updateHistoryFlags();
      }
      return;
    }
    this.push(to, current);
    this.restore(entry.elements);
  }

  private samePage(a: PageSettings, b: PageSettings): boolean {
    return (
      a.background_kind === b.background_kind &&
      a.background_template === b.background_template &&
      a.background_color === b.background_color &&
      Math.abs(a.print_scale - b.print_scale) < 1e-6 &&
      a.page_width_pt === b.page_width_pt &&
      a.page_height_pt === b.page_height_pt &&
      this.sameFile(a.file_id, b.file_id)
    );
  }

  /** The same upload, or the same file uploaded twice (an undo uploads it again). */
  private sameFile(a: string | null, b: string | null): boolean {
    if (a === b) return true;
    if (a === null || b === null) return false;
    const held = this.heldFiles.get(a);
    return held !== undefined && held === this.heldFiles.get(b);
  }

  /** A file the user uploaded here, so an undo can bring it back. */
  rememberFile(fileId: string | null, file: File) {
    if (fileId) this.heldFiles.set(fileId, file);
  }

  heldFile(fileId: string | null): File | null {
    return fileId ? (this.heldFiles.get(fileId) ?? null) : null;
  }

  /**
   * The server changed the page (and reworked the layout with it): take its answer
   * and keep the state before as one history entry. When the change deleted an
   * uploaded file we cannot bring back, the history up to here is gone with it.
   */
  commitPageChange(before: VoucherDesign, design: VoucherDesign) {
    const entry: HistoryEntry = {
      elements: JSON.stringify(this.elements()),
      page: pageSettingsOf(before),
    };
    this.design.set(design);
    const saved = (design.layout?.elements ?? []).map(cleanElement);
    // Only a new page size or print size reworks the layout on the server; after
    // any other change (a colour) the elements on screen stay, unsaved or not.
    const reworked =
      before.page_width_pt !== design.page_width_pt ||
      before.page_height_pt !== design.page_height_pt ||
      Math.abs((before.print_scale ?? 1) - (design.print_scale ?? 1)) > 1e-6;
    this.elements.set(reworked ? saved : this.elements());
    this.savedSnapshot.set(JSON.stringify(saved));
    this.serverIssues.set([]);
    const lostFile =
      entry.page?.background_kind === 'file' &&
      !this.sameFile(entry.page.file_id, pageSettingsOf(design).file_id) &&
      !this.heldFile(entry.page.file_id);
    if (lostFile) this.past = [];
    else this.push(this.past, entry);
    this.future = [];
    this.dragSnapshot = null;
    this.lastRecord = null;
    this.updateHistoryFlags();
  }

  // ------------------------------------------------------------ selection

  /** Click: select the element (and its group). With `toggle` (Shift): add or remove it. */
  select(id: string | null, toggle = false) {
    if (id === null) {
      this.selectedIds.set(new Set());
      return;
    }
    const unit = this.unitOf(id);
    this.selectedIds.update((current) => {
      if (!toggle) {
        // Already inside this group (a part of it selected, not all of it): stay
        // inside and select the clicked part, like Canva, Figma and Slides do.
        const insideGroup =
          unit.length > 1 &&
          current.size > 0 &&
          current.size < unit.length &&
          [...current].every((member) => unit.includes(member));
        if (insideGroup) return new Set([id]);
        return current.has(id) && current.size > 1 ? current : new Set(unit);
      }
      const next = new Set(current);
      const remove = unit.every((member) => next.has(member));
      for (const member of unit) remove ? next.delete(member) : next.add(member);
      return next;
    });
  }

  /**
   * A second click (no drag) on a part of the selected group selects just that
   * part; it stays in the group and is edited or moved on its own.
   */
  drillIn(id: string) {
    const unit = this.unitOf(id);
    const current = this.selectedIds();
    if (unit.length > 1 && current.size === unit.length && unit.every((m) => current.has(m))) {
      this.selectedIds.set(new Set([id]));
    }
  }

  /** Back from a part to its whole group. */
  selectGroupOf(id: string) {
    this.selectedIds.set(new Set(this.unitOf(id)));
  }

  startEditing(id: string, selectAll = false) {
    if (this.elements().find((e) => e.id === id)?.kind !== TEXT_KIND) return;
    this.selectedIds.set(new Set([id]));
    this.editSelectAll.set(selectAll);
    this.editingId.set(id);
  }

  stopEditing() {
    this.editingId.set(null);
    this.editSelectAll.set(false);
  }

  /** The marquee: everything it touched (plus their groups), added with Shift. */
  selectMany(ids: string[], add = false) {
    const next = new Set(add ? this.selectedIds() : []);
    for (const id of ids) for (const member of this.unitOf(id)) next.add(member);
    this.selectedIds.set(next);
  }

  selectAll() {
    this.selectedIds.set(new Set(this.elements().map((e) => e.id)));
  }

  /** An element and the rest of its group: what a click selects. */
  private unitOf(id: string): string[] {
    const element = this.elements().find((e) => e.id === id);
    if (!element) return [];
    return element.group ? this.membersOf(element.group).map((e) => e.id) : [id];
  }

  private membersOf(group: string): DesignElement[] {
    return this.elements().filter((e) => e.group === group);
  }

  // ---------------------------------------------------------------- groups

  /** Make the selection one group (merging groups it contains). */
  group() {
    this.record();
    const ids = this.selectedIds();
    if (ids.size < 2) return;
    const group = crypto.randomUUID();
    this.elements.update((all) => all.map((e) => (ids.has(e.id) ? { ...e, group } : e)));
  }

  ungroup() {
    this.record();
    const ids = this.selectedIds();
    this.elements.update((all) => all.map((e) => (ids.has(e.id) ? { ...e, group: null } : e)));
  }

  // ------------------------------------------------------------- arranging

  /** Align the selection: several elements among themselves, one to the page. */
  align(mode: AlignMode) {
    this.record();
    const selected = this.selectedElements();
    if (selected.length === 0) return;
    const page = this.page();
    const target =
      selected.length > 1
        ? this.selectionBounds()!
        : { x: 0, y: 0, width: page.width, height: page.height };
    this.applyRects(selected, alignRects(selected, mode, target));
  }

  distribute(axis: DistributeAxis) {
    this.record();
    const selected = this.selectedElements();
    if (selected.length < 3) return;
    this.applyRects(selected, distributeRects(selected, axis));
  }

  /** Move every selected element by (dx, dy) pt, keeping all of them on the page. */
  moveSelectedBy(dx: number, dy: number) {
    const selected = this.selectedElements();
    if (selected.length === 0) return;
    this.record('nudge');
    const page = this.page();
    const bounds = this.selectionBounds()!;
    const cx = clamp(dx, -bounds.x, page.width - bounds.x - bounds.width);
    const cy = clamp(dy, -bounds.y, page.height - bounds.y - bounds.height);
    this.applyRects(
      selected,
      selected.map((e) => ({ ...e, x: e.x + cx, y: e.y + cy })),
    );
  }

  private applyRects(targets: DesignElement[], rects: Rect[]) {
    const byId = new Map(targets.map((e, i) => [e.id, rects[i]]));
    this.elements.update((all) =>
      all.map((e) => {
        const rect = byId.get(e.id);
        if (!rect) return e;
        const entry = this.catalogByKind().get(e.kind);
        return {
          ...e,
          ...clampRect(
            {
              x: roundPt(rect.x),
              y: roundPt(rect.y),
              width: roundPt(rect.width),
              height: roundPt(rect.height),
            },
            this.page(),
            minSizeFor(entry),
          ),
        };
      }),
    );
  }

  /**
   * A new element of `kind` with the catalog's default size, cascaded from the
   * centre. A library picture (`image`) comes with its id and keeps its proportions.
   */
  addElement(
    kind: string,
    picture?: { id: string; width: number; height: number },
    shape: BoxShape = 'rect',
  ): DesignElement | null {
    const entry = this.catalogByKind().get(kind);
    if (!entry || !allowedOn(entry, this.voucherType())) return null;
    if (entry.singleton && this.elements().some((e) => e.kind === kind)) return null;
    const page = this.page();
    const offset = 12 * (this.elements().length % 8);
    const ratio = picture && picture.height > 0 ? picture.width / picture.height : null;
    const isLine = entry.render === 'box' && shape === 'line';
    const square = entry.render === 'box' && shape !== 'rect' && shape !== 'line';
    const size = {
      width: Math.min(square ? 120 : entry.default_size.width, page.width),
      height: Math.min(
        isLine
          ? 12
          : square
            ? 120
            : ratio
              ? entry.default_size.width / ratio
              : entry.default_size.height,
        page.height,
      ),
    };
    const rect = this.freeSpot(
      clampRect(
        {
          x: roundPt((page.width - size.width) / 2 + offset),
          y: roundPt((page.height - size.height) / 2 + offset),
          width: roundPt(size.width),
          height: roundPt(size.height),
        },
        page,
        minSizeFor(entry),
      ),
    );
    const textual = entry.render === 'text';
    const element: DesignElement = {
      id: crypto.randomUUID(),
      kind,
      ...rect,
      font: textual ? defaultFont() : null,
      align: 'left',
      valign: 'top',
      ...(textual ? { content: 'Text', wrap: false } : {}),
      ...(entry.colors
        ? { qr_color: '#000000', qr_background: '#ffffff', qr_quiet_zone: false }
        : {}),
      ...(picture ? { image_id: picture.id } : {}),
      ...(entry.render === 'image'
        ? {
            fit: 'cover' as const,
            shape: entry.image_source === 'logo' ? ('circle' as const) : ('rect' as const),
            border_width: entry.image_source === 'logo' ? 3 : 0,
            border_color: '#ffffff',
            border_radius: 0,
          }
        : {}),
      ...(entry.render === 'box'
        ? isLine
          ? { shape, fill: null, border_width: 2, border_color: '#000000', border_radius: 0 }
          : {
              shape,
              // a visible start on a white page: the shop colour comes via the swatches
              fill: shape === 'rect' ? '#ffffff' : '#d9d9d9',
              border_width: 0,
              border_color: '#000000',
              border_radius: 0,
            }
        : {}),
    };
    if (!this.batching) this.record();
    this.elements.update((all) => brandLast([...all, element]));
    this.selectedIds.set(new Set([element.id]));
    return element;
  }

  /**
   * The nearest place where a new box covers no other element: the spot itself,
   * else the same column further down, then further up, in steps of its height.
   * Nothing lands on the QR code or on a text by accident; a full page keeps the spot.
   */
  private freeSpot(rect: Rect): Rect {
    const page = this.page();
    const others = this.elements();
    const covers = (r: Rect) => others.some((e) => rectsIntersect(r, e));
    if (!covers(rect)) return rect;
    const step = rect.height + 8;
    for (let i = 1; i * step < page.height; i++) {
      for (const dy of [i * step, -i * step]) {
        const y = rect.y + dy;
        if (y < 0 || y + rect.height > page.height) continue;
        const candidate = { ...rect, y: roundPt(y) };
        if (!covers(candidate)) return candidate;
      }
    }
    return rect;
  }

  /** A text with ready content: a placeholder's preset ("{company_name}") or own words. */
  addText(preset: TextPreset): DesignElement | null {
    const outer = this.batching;
    if (!outer) this.record();
    this.batching = true;
    const element = this.addElement(TEXT_KIND);
    this.batching = outer;
    if (!element) return null;
    const page = this.page();
    const size = preset.size ?? { width: element.width, height: element.height };
    const width = Math.min(size.width, page.width);
    const height = Math.min(size.height, page.height);
    // the centre addElement chose, at the preset's size, in a free spot
    const spot = this.freeSpot(
      clampRect(
        {
          x: element.x + (element.width - width) / 2,
          y: element.y + (element.height - height) / 2,
          width,
          height,
        },
        page,
        { width: 20, height: 10 },
      ),
    );
    this.applyPatch(element.id, { content: preset.content, wrap: preset.wrap, ...spot });
    return this.elements().find((e) => e.id === element.id) ?? null;
  }

  /** What "add" places for a placeholder: its preset text at its default size. */
  presetOf(key: string): TextPreset {
    const placeholder = this.placeholdersByKey().get(key);
    return placeholder
      ? { content: placeholder.preset, wrap: placeholder.wrap, size: placeholder.default_size }
      : { content: `{${key}}`, wrap: false };
  }

  /**
   * Place everything required the design still lacks, stacked from the top left
   * with a little spacing, so a blank page gets usable in one click.
   */
  addRequired(): string[] {
    const missing = this.checklist().filter((item) => !item.placedId);
    if (missing.length === 0) return [];
    this.record();
    this.batching = true;
    const page = this.page();
    const margin = Math.round(Math.min(page.width, page.height) * 0.06);
    let y = margin;
    const ids: string[] = [];
    for (const item of missing) {
      const size =
        item.kind === 'element'
          ? this.catalogByKind().get(item.key)?.default_size
          : this.placeholdersByKey().get(item.key)?.default_size;
      if (!size) continue;
      const width = Math.min(size.width, page.width - 2 * margin);
      const height = Math.min(size.height, page.height - 2 * margin);
      if (y + height > page.height - margin) y = margin; // a second column would be odd; wrap
      const element =
        item.kind === 'element' ? this.addElement(item.key) : this.addText(this.presetOf(item.key));
      if (!element) continue;
      this.applyPatch(element.id, { x: margin, y, width, height });
      ids.push(element.id);
      y += height + 12;
    }
    this.batching = false;
    this.selectedIds.set(new Set(ids));
    return ids;
  }

  /**
   * Change one element. A `transient` patch (a drag in progress) records nothing;
   * the drag's start is remembered and becomes one history entry on `setRect`.
   */
  patchElement(id: string, patch: Partial<DesignElement>, transient = false) {
    if (transient) {
      this.dragSnapshot ??= JSON.stringify(this.elements());
    } else {
      this.record(`patch:${id}:${Object.keys(patch).sort().join(',')}`);
    }
    this.applyPatch(id, patch);
  }

  private applyPatch(id: string, patch: Partial<DesignElement>) {
    for (const key of ['x', 'y', 'width', 'height'] as const) {
      if (patch[key] != null) patch = { ...patch, [key]: roundPt(patch[key]!) };
    }
    this.elements.update((all) =>
      all.map((e) => {
        if (e.id !== id) return e;
        const merged = cleanElement({ ...e, ...patch });
        const entry = this.catalogByKind().get(e.kind);
        const rect = clampRect(merged, this.page(), minSizeFor(entry));
        return { ...merged, ...rect };
      }),
    );
  }

  /**
   * Change every selected element that `accept` takes, as one history entry: the
   * selection panel's shared controls. Elements the patch does not apply to (a
   * picture when the font changes) are simply left alone.
   */
  patchSelection(
    patch: SelectionPatch,
    accept: (e: DesignElement, entry: CatalogEntry | undefined) => boolean = () => true,
    key?: string,
  ) {
    const ids = this.selectedIds();
    const targets = new Set(
      this.elements()
        .filter((e) => ids.has(e.id) && accept(e, this.catalogByKind().get(e.kind)))
        .map((e) => e.id),
    );
    if (targets.size === 0) return;
    this.record(key ? `selection:${key}` : undefined);
    const { font, ...rest } = patch;
    this.elements.update((all) =>
      all.map((e) => {
        if (!targets.has(e.id)) return e;
        const merged = cleanElement({
          ...e,
          ...rest,
          ...(font && e.font ? { font: { ...e.font, ...font } } : {}),
        });
        const entry = this.catalogByKind().get(e.kind);
        return { ...merged, ...clampRect(merged, this.page(), minSizeFor(entry)) };
      }),
    );
  }

  /** Grow or shrink the font of every selected text element by `delta` pt (⇧⌘. / ⇧⌘,). */
  adjustFontSize(delta: number) {
    const ids = this.selectedIds();
    const limits = this.catalog()?.limits;
    const min = limits?.font_size_min ?? 6;
    const max = limits?.font_size_max ?? 120;
    const targets = this.elements().filter((e) => ids.has(e.id) && e.font);
    if (targets.length === 0) return;
    this.record('fontsize');
    this.elements.update((all) =>
      all.map((e) => {
        if (!ids.has(e.id) || !e.font) return e;
        const size = Math.min(max, Math.max(min, Math.round(e.font.size + delta)));
        return size === e.font.size ? e : { ...e, font: { ...e.font, size } };
      }),
    );
  }

  /** Move the selection by (dx, dy) pt (arrow keys). */
  nudge(dx: number, dy: number) {
    this.moveSelectedBy(dx, dy);
  }

  /** The end of a drag or a keyboard resize: one history entry for the whole drag. */
  setRect(id: string, rect: Rect) {
    this.commitPatch(id, {
      x: roundPt(rect.x),
      y: roundPt(rect.y),
      width: roundPt(rect.width),
      height: roundPt(rect.height),
    });
  }

  /** The end of any drag that changed an element transiently (a move, a crop pan). */
  commitPatch(id: string, patch: Partial<DesignElement>) {
    if (this.dragSnapshot !== null) {
      this.push(this.past, { ...this.snapshot(), elements: this.dragSnapshot });
      this.future = [];
      this.dragSnapshot = null;
      this.lastRecord = null;
      this.updateHistoryFlags();
    } else {
      this.record();
    }
    this.applyPatch(id, patch);
  }

  // ------------------------------------------------------------ copy & paste

  /** The selected elements as they are, for the clipboard or a duplicate. */
  copySelection(): DesignElement[] {
    const ids = this.selectedIds();
    return this.elements()
      .filter((e) => ids.has(e.id))
      .map((e) => cleanElement(e));
  }

  /**
   * Copies of `sources` on this page, a little down and to the right of the
   * originals, with new ids; elements grouped among themselves stay a group. What
   * cannot be added is left out: a second QR code, logo or banner, the Schenkwerk
   * logo, and product values on a money design. Returns the new ids.
   */
  paste(sources: DesignElement[], offset = 12): string[] {
    const page = this.page();
    const type = this.voucherType();
    const known = this.placeholdersByKey();
    const placed = new Set(this.elements().map((e) => e.kind));
    const groups = new Map<string, string>();
    const pasted: DesignElement[] = [];
    for (const source of sources) {
      const entry = this.catalogByKind().get(source.kind);
      if (!entry || entry.kind === BRAND_KIND || !allowedOn(entry, type)) continue;
      if (entry.singleton && placed.has(entry.kind)) continue;
      const productValue = placeholderKeys(source.content ?? '', known).some(
        (key) => known.get(key)?.product_only && type !== 'product',
      );
      if (productValue) continue;
      placed.add(entry.kind);
      let group: string | null = null;
      if (source.group) {
        group = groups.get(source.group) ?? crypto.randomUUID();
        groups.set(source.group, group);
      }
      const rect = clampRect(
        {
          x: source.x + offset,
          y: source.y + offset,
          width: source.width,
          height: source.height,
        },
        page,
        minSizeFor(entry),
      );
      pasted.push(cleanElement({ ...source, ...rect, id: crypto.randomUUID(), group }));
    }
    if (pasted.length === 0) return [];
    this.record();
    this.elements.update((all) => brandLast([...all, ...pasted]));
    this.selectedIds.set(new Set(pasted.map((e) => e.id)));
    return pasted.map((e) => e.id);
  }

  /** The selection once more, next to itself. */
  duplicateSelection(): string[] {
    return this.paste(this.copySelection());
  }

  /** The selection for the clipboard, and gone from the page. */
  cutSelection(): DesignElement[] {
    const copies = this.copySelection();
    if (copies.length) this.removeSelected();
    return copies;
  }

  /** Remove an element; when it is part of the selection, the whole selection. */
  remove(id: string) {
    this.record();
    const ids = this.selectedIds().has(id) ? this.selectedIds() : new Set([id]);
    this.elements.update((all) => all.filter((e) => !ids.has(e.id)));
    this.selectedIds.update((current) => {
      const next = new Set(current);
      for (const removed of ids) next.delete(removed);
      return next;
    });
  }

  removeSelected() {
    if (this.selectedIds().size === 0) return;
    this.record();
    const ids = this.selectedIds();
    this.elements.update((all) => all.filter((e) => !ids.has(e.id)));
    this.selectedIds.set(new Set());
  }

  /** The list order is the stacking order: later elements print on top. */
  move(id: string, delta: number) {
    this.record();
    this.elements.update((all) => {
      const from = all.findIndex((e) => e.id === id);
      if (from < 0) return all;
      const to = Math.min(Math.max(from + delta, 0), all.length - 1);
      if (to === from) return all;
      const next = [...all];
      const [element] = next.splice(from, 1);
      next.splice(to, 0, element);
      return brandLast(next); // nothing moves above the logo, and the logo stays on top
    });
  }

  indexOf(id: string): number {
    return this.elements().findIndex((e) => e.id === id);
  }

  markSaved(design: VoucherDesign) {
    this.design.set(design);
    // A save answers with what was sent; a print-size or background change answers
    // with a reworked layout the history would not fit any more.
    const elements = (design.layout?.elements ?? []).map(cleanElement);
    if (JSON.stringify(elements) !== JSON.stringify(this.elements())) this.clearHistory();
    this.elements.set(elements);
    this.savedSnapshot.set(JSON.stringify(elements));
    this.serverIssues.set([]);
  }

  /** Backend layout errors (`loc` = ["elements", index, ...]) as issues on elements. */
  setServerIssues(issues: LayoutIssue[]) {
    const elements = this.elements();
    this.serverIssues.set(
      issues.map((issue) => {
        const index =
          issue.loc[0] === 'elements' && typeof issue.loc[1] === 'number' ? issue.loc[1] : -1;
        return { code: 'server', message: issue.msg, elementId: elements[index]?.id };
      }),
    );
  }

  setFitScale(fit: number, fitWidth = fit) {
    if (!(fit > 0 && Number.isFinite(fit))) return;
    this.fitScale.set(fit);
    // Open at real size; a page too wide for the viewport opens as wide as it can.
    if (this.zoomPending) {
      this.zoomPending = false;
      this.zoom.set(Math.min(1, fitWidth));
    }
  }

  setZoom(zoom: number) {
    this.zoom.set(Math.min(Math.max(zoom, 0.1), 5));
  }

  fitToView() {
    this.setZoom(this.fitScale());
  }
}

export function defaultFont(): ElementFont {
  return { family: 'sans', size: 14, bold: false, italic: false, color: '#000000' };
}

/**
 * Only the fields the backend's layout schema knows (it forbids any other). Guards
 * against stray properties, such as a DOM event merged in by mistake.
 */
export function cleanElement(e: DesignElement): DesignElement {
  const font = e.font
    ? {
        family: e.font.family,
        size: e.font.size,
        bold: e.font.bold,
        italic: e.font.italic,
        color: e.font.color,
        ...(e.font.line_height != null ? { line_height: e.font.line_height } : {}),
      }
    : null;
  const frame = {
    ...(e.border_width != null ? { border_width: e.border_width } : {}),
    ...(e.border_color != null ? { border_color: e.border_color } : {}),
    ...(e.border_radius != null ? { border_radius: e.border_radius } : {}),
  };
  return {
    id: e.id,
    kind: e.kind,
    x: e.x,
    y: e.y,
    width: e.width,
    height: e.height,
    ...(e.group ? { group: e.group } : {}),
    font,
    align: e.align,
    valign: e.valign,
    // a one-line text has no line breaks (the backend does the same)
    ...(e.kind === TEXT_KIND
      ? {
          content: e.wrap ? (e.content ?? '') : (e.content ?? '').replace(/[\r\n]+/g, ' '),
          wrap: !!e.wrap,
        }
      : {}),
    ...(e.kind === 'qr_code'
      ? {
          qr_color: e.qr_color ?? '#000000',
          qr_background: e.qr_background ?? '#ffffff',
          qr_quiet_zone: e.qr_quiet_zone ?? false,
        }
      : {}),
    ...(IMAGE_KINDS.has(e.kind)
      ? {
          ...(e.kind === 'image' ? { image_id: e.image_id ?? null } : {}),
          ...(e.fit ? { fit: e.fit } : {}),
          ...(e.focus_x != null ? { focus_x: e.focus_x } : {}),
          ...(e.focus_y != null ? { focus_y: e.focus_y } : {}),
          ...(e.shape ? { shape: e.shape } : {}),
          ...frame,
        }
      : {}),
    ...(e.kind === 'box' ? { fill: e.fill ?? null, shape: e.shape ?? 'rect', ...frame } : {}),
    ...(e.opacity != null && e.opacity < 1 ? { opacity: e.opacity } : {}),
    ...(e.kind === BRAND_KIND ? { brand_color: e.brand_color ?? null } : {}),
  };
}

/** The Schenkwerk logo: required, and always painted last so nothing covers it. */
export const BRAND_KIND = 'schenkwerk_logo';
/** The one text kind: own words and placeholders in its content. */
export const TEXT_KIND = 'text';

/** The elements in paint order with the logo on top (the backend sorts the same way). */
export function brandLast(elements: DesignElement[]): DesignElement[] {
  const brand = elements.filter((e) => e.kind === BRAND_KIND);
  return brand.length ? [...elements.filter((e) => e.kind !== BRAND_KIND), ...brand] : elements;
}

/** Kinds the engine renders as an image (the backend's `render == "image"`). */
export const IMAGE_KINDS = new Set(['image', 'company_logo', 'company_banner', 'product_image']);
